import {
  getHouseholdMemberPhoto,
  removeHouseholdMemberPhoto,
  uploadHouseholdMemberPhoto,
  type HouseholdMemberPhotoContract,
} from '@urbanik/api-client';
import { AvatarUpload, CanonicalModal } from '@urbanik/ui';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';

export function HouseholdMemberPhotoField({
  memberId,
  householdId,
  householdMemberId,
  initials,
  isMinor,
  canRead,
  canManage,
}: {
  memberId: string;
  householdId: string;
  householdMemberId: string;
  initials: string;
  isMinor: boolean;
  canRead: boolean;
  canManage: boolean;
}) {
  const [photo, setPhoto] = useState<HouseholdMemberPhotoContract | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [revision, setRevision] = useState(0);
  const load = () => {
    if (!canRead) return;
    setError('');
    void getHouseholdMemberPhoto(apiBase, memberId, householdId, householdMemberId)
      .then((result) => setPhoto(result.photo))
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : 'No pudimos cargar la fotografía.'),
      );
  };
  useEffect(load, [memberId, householdId, householdMemberId, canRead]);
  if (!canRead) return null;
  const previewUrl = photo
    ? `${apiBase.replace(/\/$/, '')}${photo.thumbnailUrl}${photo.thumbnailUrl.includes('?') ? '&' : '?'}v=${revision}`
    : null;
  const select = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const result = await uploadHouseholdMemberPhoto(
        apiBase,
        memberId,
        householdId,
        householdMemberId,
        file,
      );
      setPhoto(result.photo);
      setRevision((value) => value + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No pudimos guardar la fotografía.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!photo) return;
    setBusy(true);
    setError('');
    try {
      await removeHouseholdMemberPhoto(
        apiBase,
        memberId,
        householdId,
        householdMemberId,
        photo.id,
        reason,
      );
      setPhoto(null);
      setRemoveOpen(false);
      setReason('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No pudimos retirar la fotografía.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="household-member-photo">
      <AvatarUpload
        label="Fotografía del integrante"
        fallbackText={initials}
        previewUrl={previewUrl}
        status={busy ? 'loading' : error ? 'error' : photo ? 'success' : 'empty'}
        error={error}
        {...(isMinor
          ? {
              warning:
                'La persona es menor de edad. Confirma la autorización aplicable antes de registrar su fotografía.',
            }
          : {})}
        readOnly={!canManage}
        canRemove={canManage && Boolean(photo)}
        onFileChange={(file) => void select(file)}
        onRemove={() => setRemoveOpen(true)}
        onRetry={load}
        imageAlt="Fotografía administrativa del integrante"
      />
      <CanonicalModal
        open={removeOpen}
        size="medium"
        eyebrow="Información sensible"
        title="Retirar fotografía"
        description="La imagen dejará de mostrarse, pero conservará su trazabilidad protegida."
        onClose={() => !busy && setRemoveOpen(false)}
      >
        <form
          className="household-member-photo-remove"
          onSubmit={(event) => {
            event.preventDefault();
            void remove();
          }}
        >
          <label className="ui-field">
            <span>Motivo del retiro *</span>
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {error ? (
            <p className="member-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="member-status-actions">
            <button
              type="button"
              className="ui-button"
              onClick={() => setRemoveOpen(false)}
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              className="ui-button ui-button--primary"
              disabled={busy || reason.trim().length < 10}
            >
              {busy ? 'Retirando…' : 'Confirmar retiro'}
            </button>
          </div>
        </form>
      </CanonicalModal>
    </div>
  );
}
